import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, Plus, KeyRound, Copy, RefreshCcw, Trash2, X, Code2,
  BookOpen, Webhook, ReceiptText, Eye, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";
import { fmtDateTime } from "../../lib/ist";

const copyText = async (t, label = "Copied") => {
  try { await navigator.clipboard.writeText(t); toast.success(label); }
  catch { toast.error("Could not copy"); }
};

function SecretReveal({ data, onClose }) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-slate-900 border border-amber-500/40 rounded-2xl p-6" data-testid="devapi-secret-modal">
          <h3 className="text-white font-bold text-lg">Save these credentials now</h3>
          <p className="text-amber-300 text-xs mt-1">The API secret is shown only once — it cannot be retrieved again.</p>
          <div className="mt-4 space-y-3">
            {[["API Key", data.key_id], ["API Secret", data.secret], ["Webhook Secret", data.webhook_secret]].filter(([,v])=>v).map(([k,v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{k}</div>
                <div className="mt-1 flex items-center gap-2">
                  <input readOnly value={v} className="flex-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-emerald-300 font-mono text-xs" data-testid={`devapi-reveal-${k.toLowerCase().replace(/\s/g,"-")}`}/>
                  <button onClick={()=>copyText(v, `${k} copied`)} className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white"><Copy size={14}/></button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={onClose} className="mt-5 w-full py-2.5 rounded-xl bg-blue-600 text-white font-bold" data-testid="devapi-secret-done">I&apos;ve saved them — close</button>
        </div>
      </div>
    </ModalPortal>
  );
}

function CreateKeyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", allowed_gateways: ["razorpay", "payglocal"], webhook_url: "" });
  const [busy, setBusy] = useState(false);
  const toggleGw = (g) => setForm(f => ({
    ...f, allowed_gateways: f.allowed_gateways.includes(g)
      ? f.allowed_gateways.filter(x => x !== g) : [...f.allowed_gateways, g] }));
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.allowed_gateways.length === 0) { toast.error("Select at least one gateway"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dev/keys", { ...form, webhook_url: form.webhook_url || null });
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create key"); }
    finally { setBusy(false); }
  };
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
        <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="devapi-create-modal">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-lg">New API key</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18}/></button>
          </div>
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs text-slate-400">Partner / integration name</span>
              <input value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))}
                placeholder="e.g. Acme Agency website" className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm" data-testid="devapi-create-name"/>
            </label>
            <div>
              <span className="text-xs text-slate-400">Allowed gateways</span>
              <div className="flex gap-2 mt-1.5">
                {["razorpay","payglocal"].map(g => (
                  <button key={g} onClick={()=>toggleGw(g)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border ${form.allowed_gateways.includes(g) ? "bg-emerald-600/20 border-emerald-500 text-emerald-300" : "bg-slate-800 border-slate-700 text-slate-500"}`}
                    data-testid={`devapi-create-gw-${g}`}>{g === "razorpay" ? "Razorpay" : "PayGlocal"}</button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-xs text-slate-400">Default webhook URL (optional)</span>
              <input value={form.webhook_url} onChange={(e)=>setForm(f=>({...f, webhook_url:e.target.value}))}
                placeholder="https://partner-site.com/webhooks/optivize" className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-white text-sm" data-testid="devapi-create-webhook"/>
            </label>
          </div>
          <button onClick={submit} disabled={busy} className="mt-5 w-full py-2.5 rounded-xl bg-blue-600 text-white font-bold inline-flex items-center justify-center gap-2" data-testid="devapi-create-submit">
            {busy ? <Loader2 size={15} className="animate-spin"/> : <KeyRound size={15}/>} Create key
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

function LogsPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/dev/logs").then(r=>setRows(r.data.rows)).catch(()=>setRows([])); }, []);
  if (!rows) return <Loader2 className="animate-spin text-blue-400 mx-auto my-8"/>;
  return (
    <div className="card overflow-x-auto" data-testid="devapi-logs">
      {rows.length === 0 ? <p className="text-slate-500 text-sm p-6 text-center">No API activity yet.</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
            <tr><th className="py-2.5 px-3 text-left">When (IST)</th><th className="text-left px-2">Key</th><th className="text-left px-2">Action</th><th className="text-left px-2">Status</th><th className="text-left px-2">Ref / URL</th><th className="text-left px-2">IP</th></tr>
          </thead>
          <tbody>
            {rows.map(l => (
              <tr key={l.id} className="border-b border-slate-800">
                <td className="py-2 px-3 text-slate-400 whitespace-nowrap">{fmtDateTime(l.at)}</td>
                <td className="px-2 font-mono text-xs text-cyan-300">{l.key_id?.slice(0, 16)}…</td>
                <td className="px-2 font-mono text-xs text-slate-300">{l.action}</td>
                <td className="px-2"><span className={`text-xs font-bold ${(l.status || l.status_code) < 400 ? "text-emerald-400" : "text-rose-400"}`}>{l.status || l.status_code || "—"}</span></td>
                <td className="px-2 font-mono text-[11px] text-slate-500 max-w-[220px] truncate">{l.ref || l.url || "—"}</td>
                <td className="px-2 text-slate-500 text-xs">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TxnDetailModal({ t, onClose }) {
  const c = t.customer || {};
  const cd = t.customer_details || {};
  const Row = ({ k, v, mono }) => !v ? null : (
    <div className="flex justify-between gap-3 border-b border-slate-800 py-1.5 text-sm">
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className={`text-right break-all ${mono ? "font-mono text-xs text-cyan-300" : "text-slate-200 font-semibold"}`}>{v}</span>
    </div>
  );
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[110] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl p-6 my-6" onClick={(e)=>e.stopPropagation()} data-testid="devapi-txn-modal">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">API Transaction</div>
              <h3 className="text-white font-bold">{t.product_name}</h3>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="devapi-txn-close"><X size={18}/></button>
          </div>
          <div className="mt-4">
            <Row k="Status" v={t.payment_status === "success" ? "PAID" : t.payment_status?.toUpperCase()}/>
            <Row k="Amount" v={`${t.currency} ${t.amount}`}/>
            <Row k="Gateway" v={t.gateway === "payglocal" ? "PayGlocal" : "Razorpay"}/>
            <Row k="Gateway Transaction ID" v={t.transaction_id} mono/>
            <Row k="Razorpay Order ID" v={t.razorpay_order_id} mono/>
            <Row k="Razorpay Payment Link ID" v={t.razorpay_plink_id} mono/>
            <Row k="PayGlocal GID" v={t.payglocal_gid} mono/>
            <Row k="PayGlocal Merchant Txn" v={t.payglocal_merchant_txn_id} mono/>
            <Row k="Invoice #" v={t.invoice_number} mono/>
            <Row k="API Key" v={`${t.api_key_name} (${t.api_key_id?.slice(0,18)}…)`}/>
            <Row k="Partner Reference ID" v={t.reference_id} mono/>
            <Row k="Checkout mode" v={t.checkout_mode || (t.auto_checkout ? "auto" : "hosted_page")}/>
            <Row k="Partner redirect URL" v={t.redirect_url} mono/>
            <Row k="Partner webhook URL" v={t.webhook_url} mono/>
            <Row k="Created" v={fmtDateTime(t.created_at)}/>
            <Row k="Paid at" v={t.paid_at ? fmtDateTime(t.paid_at) : null}/>
            <Row k="Expires" v={t.expires_at && t.payment_status === "pending" ? fmtDateTime(t.expires_at) : null}/>
            <Row k="Last gateway sync" v={t.status_synced_at ? fmtDateTime(t.status_synced_at) : null}/>
          </div>
          {t.gateway_last_response && (
            <div className="mt-4 pt-3 border-t border-slate-700">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Payment gateway response</div>
              <pre className="bg-slate-950 border border-slate-800 text-cyan-300 text-[11px] rounded-lg p-3 overflow-x-auto max-h-44" data-testid="devapi-txn-gwresp">{JSON.stringify(t.gateway_last_response, null, 2)}</pre>
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-slate-700">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Customer details</div>
            <Row k="Name" v={c.name || [cd.first_name, cd.last_name].filter(Boolean).join(" ")}/>
            <Row k="Email" v={c.email || cd.email}/>
            <Row k="Phone" v={c.phone || cd.phone}/>
            <Row k="Address" v={[c.address_street || cd.address, c.address_city || cd.city, c.address_state || cd.state, c.postal_code || cd.pin_code, c.address_country || cd.country].filter(Boolean).join(", ")}/>
          </div>
          {t.payment_status === "success" && t.invoice_id && (
            <a href={`/api/pg/links/${t.token}/invoice.pdf`} download
              className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm"
              data-testid="devapi-txn-invoice"><FileDown size={15}/> Download Invoice / Receipt PDF</a>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

const TXN_BADGE = {
  success: "bg-emerald-600/20 text-emerald-300 border-emerald-500/50",
  pending: "bg-amber-600/20 text-amber-300 border-amber-500/50",
  failed: "bg-rose-600/20 text-rose-300 border-rose-500/50",
  declined: "bg-rose-600/20 text-rose-300 border-rose-500/50",
  cancelled: "bg-slate-700 text-slate-400 border-slate-600",
};
const TXN_LABEL = { success: "PAID", pending: "PENDING", failed: "FAILED", declined: "DECLINED", cancelled: "CANCELLED" };

function TransactionsPanel() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [gw, setGw] = useState("");
  const [loading, setLoading] = useState(false);
  const load = (gateway = gw) => {
    setLoading(true);
    api.get(`/admin/dev/transactions${gateway ? `?gateway=${gateway}` : ""}`)
      .then(r=>setRows(r.data.rows)).catch(()=>setRows([])).finally(()=>setLoading(false));
  };
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return <Loader2 className="animate-spin text-blue-400 mx-auto my-8"/>;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {[["","All gateways"],["razorpay","Razorpay"],["payglocal","PayGlocal"]].map(([id,label]) => (
          <button key={id} onClick={()=>{ setGw(id); load(id); }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${gw===id ? "bg-blue-600 border-blue-600 text-white" : "bg-slate-800/60 border-slate-700 text-slate-400 hover:border-blue-500/50"}`}
            data-testid={`devapi-txn-gw-${id || "all"}`}>{label}</button>
        ))}
        <button onClick={()=>load()} disabled={loading}
          className="ml-auto px-3 py-1.5 rounded-full text-xs font-bold border bg-slate-800/60 border-slate-700 text-slate-300 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="devapi-txn-refresh">
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""}/> {loading ? "Syncing with gateways…" : "Sync status"}
        </button>
      </div>
      <div className="card overflow-x-auto" data-testid="devapi-transactions">
      {rows.length === 0 ? <p className="text-slate-500 text-sm p-6 text-center">No API transactions yet.</p> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
            <tr><th className="py-2.5 px-3 text-left">Created (IST)</th><th className="text-left px-2">Key</th><th className="text-left px-2">Product</th><th className="text-right px-2">Amount</th><th className="text-left px-2">Gateway</th><th className="text-left px-2">Customer</th><th className="text-left px-2">Gateway Txn ID</th><th className="text-left px-2">Status</th><th className="text-right px-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(t => {
              const c = t.customer || {}; const cd = t.customer_details || {};
              return (
                <tr key={t.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2 px-3 text-slate-400 whitespace-nowrap text-xs">{fmtDateTime(t.created_at)}</td>
                  <td className="px-2 text-slate-300 text-xs">{t.api_key_name}</td>
                  <td className="px-2 text-white font-semibold max-w-[160px] truncate">{t.product_name}</td>
                  <td className="px-2 text-right text-slate-200 whitespace-nowrap">{t.currency} {t.amount}</td>
                  <td className="px-2 text-xs text-slate-300">{t.gateway === "payglocal" ? "PayGlocal" : "Razorpay"}</td>
                  <td className="px-2 text-xs text-slate-400 max-w-[160px] truncate">{c.name || [cd.first_name, cd.last_name].filter(Boolean).join(" ") || cd.email || "—"}</td>
                  <td className="px-2 font-mono text-[11px] text-cyan-300 max-w-[130px] truncate">{t.transaction_id || "—"}</td>
                  <td className="px-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TXN_BADGE[t.payment_status] || TXN_BADGE.pending}`} data-testid={`devapi-txn-status-${t.id.slice(0,6)}`}>{TXN_LABEL[t.payment_status] || t.payment_status}</span></td>
                  <td className="px-3 text-right whitespace-nowrap">
                    <button title="View full details" onClick={()=>setDetail(t)} className="p-1.5 text-slate-300 hover:text-white" data-testid={`devapi-txn-view-${t.id.slice(0,6)}`}><Eye size={14}/></button>
                    {t.payment_status === "success" && t.invoice_id && (
                      <a title="Download invoice/receipt PDF" href={`/api/pg/links/${t.token}/invoice.pdf`} download className="p-1.5 text-emerald-400 hover:text-emerald-300 inline-block" data-testid={`devapi-txn-inv-${t.id.slice(0,6)}`}><FileDown size={14}/></a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {detail && <TxnDetailModal t={detail} onClose={()=>setDetail(null)}/>}
      </div>
    </div>
  );
}

export default function AdminDevAPI() {
  const [keys, setKeys] = useState(null);
  const [tab, setTab] = useState("keys");
  const [createOpen, setCreateOpen] = useState(false);
  const [reveal, setReveal] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/dev/keys").then(r=>setKeys(r.data)).catch(()=>toast.error("Failed to load keys"));
  }, []);
  useEffect(load, [load]);

  const patch = async (id, body, ok) => {
    try { await api.patch(`/admin/dev/keys/${id}`, body); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleGateway = (k, gw) => {
    const cur = k.allowed_gateways || [];
    const next = cur.includes(gw) ? cur.filter(g => g !== gw) : [...cur, gw];
    if (next.length === 0) { toast.error("At least one gateway must stay enabled for this key"); return; }
    patch(k.id, { allowed_gateways: next },
      `${gw === "razorpay" ? "Razorpay" : "PayGlocal"} ${cur.includes(gw) ? "disabled" : "enabled"} for ${k.name}`);
  };
  const roll = async (id) => {
    if (!window.confirm("Roll this key's secret? The old secret stops working immediately.")) return;
    try { const { data } = await api.post(`/admin/dev/keys/${id}/roll`); setReveal(data); }
    catch { toast.error("Roll failed"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Partner requests will be rejected.")) return;
    try { await api.delete(`/admin/dev/keys/${id}`); toast.success("Key revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-devapi">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tag mb-2 inline-flex items-center gap-1.5"><Code2 size={12}/> Developer API</div>
          <h1 className="text-2xl font-extrabold text-white" style={{fontFamily:"Outfit"}}>API Keys & Integrations</h1>
          <p className="text-slate-400 text-sm">
            3rd-party payment API for Razorpay &amp; PayGlocal · Docs at{" "}
            <a href="/developers" target="_blank" rel="noreferrer" className="text-cyan-300 underline inline-flex items-center gap-1" data-testid="devapi-docs-link"><BookOpen size={12}/> optivize.digital/developers</a>
          </p>
        </div>
        <div className="flex gap-2">
          {[["keys","API Keys"],["transactions","Transactions"],["logs","Activity Log"]].map(([id,label]) => (
            <button key={id} onClick={()=>setTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold border ${tab===id ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}
              data-testid={`devapi-tab-${id}`}>{label}</button>
          ))}
          <button onClick={()=>setCreateOpen(true)} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid="devapi-new-key">
            <Plus size={14}/> New API key
          </button>
        </div>
      </div>

      {tab === "logs" && <LogsPanel/>}
      {tab === "transactions" && <TransactionsPanel/>}

      {tab === "keys" && (!keys ? <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-blue-400"/></div> :
        keys.length === 0 ? (
          <div className="card p-10 text-center">
            <KeyRound size={32} className="mx-auto text-slate-600"/>
            <p className="text-slate-400 mt-3 text-sm">No API keys yet. Create one to let a partner website collect payments through your gateways.</p>
          </div>
        ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-2.5 px-3 text-left">Name</th>
                <th className="text-left px-2">Key ID</th>
                <th className="text-left px-2">Gateways</th>
                <th className="text-left px-2">Webhook</th>
                <th className="text-right px-2">Usage</th>
                <th className="text-left px-2">Last used</th>
                <th className="text-left px-2">Status</th>
                <th className="text-right px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                  <td className="py-2.5 px-3 text-white font-semibold">{k.name}</td>
                  <td className="px-2">
                    <button onClick={()=>copyText(k.key_id, "Key ID copied")} className="font-mono text-xs text-cyan-300 inline-flex items-center gap-1 hover:text-cyan-200">
                      {k.key_id?.slice(0, 18)}… <Copy size={10}/>
                    </button>
                  </td>
                  <td className="px-2">
                    <div className="flex gap-1">
                      {["razorpay","payglocal"].map(gw => {
                        const on = (k.allowed_gateways || []).includes(gw);
                        return (
                          <button key={gw} onClick={()=>toggleGateway(k, gw)}
                            title={`${on ? "Disable" : "Enable"} ${gw} for this key`}
                            className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide border transition-colors ${on ? "bg-emerald-600/20 border-emerald-500/60 text-emerald-300" : "bg-slate-800 border-slate-700 text-slate-600 line-through"}`}
                            data-testid={`devapi-gw-${gw}-${k.id.slice(0,6)}`}>
                            {gw === "razorpay" ? "Razorpay" : "PayGlocal"}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-2 text-[11px] font-mono text-slate-500 max-w-[160px] truncate">{k.webhook_url || "—"}</td>
                  <td className="px-2 text-right text-slate-300">{k.usage_count || 0}</td>
                  <td className="px-2 text-slate-500 text-xs whitespace-nowrap">{k.last_used_at ? fmtDateTime(k.last_used_at) : "never"}</td>
                  <td className="px-2">
                    <button onClick={()=>patch(k.id, { active: !k.active }, k.active ? `${k.name} disabled` : `${k.name} enabled`)}
                      title={k.active ? "Disable key" : "Enable key"}
                      className="inline-flex items-center gap-2" data-testid={`devapi-active-${k.id.slice(0,6)}`}>
                      <span className={`w-9 h-5 rounded-full relative transition-colors ${k.active ? "bg-emerald-500" : "bg-slate-600"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${k.active ? "left-[18px]" : "left-0.5"}`}/>
                      </span>
                      <span className={`text-[10px] font-bold uppercase ${k.active ? "text-emerald-300" : "text-rose-300"}`}>
                        {k.active ? "active" : k.revoked_at ? "revoked" : "disabled"}
                      </span>
                    </button>
                  </td>
                  <td className="px-3 text-right whitespace-nowrap">
                    <button title="Copy webhook secret" onClick={()=>copyText(k.webhook_secret, "Webhook secret copied")} className="p-1.5 text-slate-400 hover:text-white" data-testid={`devapi-whsec-${k.id.slice(0,6)}`}><Webhook size={14}/></button>
                    <button title="Roll secret" onClick={()=>roll(k.id)} className="p-1.5 text-amber-400 hover:text-amber-300" data-testid={`devapi-roll-${k.id.slice(0,6)}`}><RefreshCcw size={14}/></button>
                    <button title="Revoke" onClick={()=>revoke(k.id)} className="p-1.5 text-rose-400 hover:text-rose-300" data-testid={`devapi-revoke-${k.id.slice(0,6)}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {createOpen && <CreateKeyModal onClose={()=>setCreateOpen(false)} onCreated={(d)=>{ setCreateOpen(false); setReveal(d); load(); }}/>}
      {reveal && <SecretReveal data={reveal} onClose={()=>setReveal(null)}/>}
    </div>
  );
}
